import * as Sentry from "@sentry/react";
import store from "../store/index";

/**
 * Initialize Sentry SDK with distributed tracing, replays, and performance monitoring
 * Environment-based configuration:
 * - Development: Full logging, all traces, all errors
 * - Production: Focus on production-level issues only, error-only replay
 */
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY;
  
  // Get environment from Vite (development/production)
  const environment = import.meta.env.MODE || "development";
  const isDevelopment = environment === "development";
  const isProduction = environment === "production";
  
  // Get API base URL from environment or use default
  const apiBaseUrl = import.meta.env.VITE_API_V2_URL || "";
  
  // Environment-based configuration
  const config = {
    dsn,
    environment,
    release: import.meta.env.VITE_SENTRY_RELEASE || "app-dashboard@0.1.0",
    
    // Integrations
    integrations: [
      Sentry.browserTracingIntegration({
        tracePropagationTargets: [
          apiBaseUrl,
          /^https?:\/\/localhost/,
          /^\/v2\//,
          /^\/api\//,
        ],
        // Exclude live orders endpoint from automatic tracing
        shouldCreateSpanForRequest(url) {
          // Don't trace the live orders polling endpoint
          if (url.includes("/v2/orders/live")) {
            return false;
          }
          return true;
        },
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    
    // Performance Monitoring - Lower rate in production
    tracesSampleRate: isProduction ? 0.1 : 1.0, // 10% in production, 100% in development
    
    // Session Replay - Only capture errors (no regular session replays)
    replaysSessionSampleRate: 0, // 0% - Don't capture regular sessions
    replaysOnErrorSampleRate: 1.0, // 100% - Capture all sessions with errors
    
    // Logging - Only enable in development
    enableLogs: isDevelopment,
    
    // Before send hook - Filter events based on environment
    beforeSend(event, hint) {
      // Add custom tags
      event.tags = {
        ...event.tags,
        platform: "web",
        client: "browser",
        version: "0.1.0",
      };
      
      // Ensure user context is set if restaurantInfo is available
      if (store.restaurantInfo) {
        const userId = store.restaurantInfo.id?.toString() || null;
        const email = store.restaurantInfo.email || null;
        const username = store.restaurantInfo.name || null;
        
        Sentry.setUser({
          id: userId,
          email: email,
          username: username,
        });
      }
      
      // In production, focus on production-level issues only
      if (isProduction) {
        // Filter out development-level logs and info messages
        if (event.level === "info" || event.level === "debug" || event.level === "log") {
          // Only allow error, warning, and fatal levels in production
          return null;
        }
        
        // Filter out console.log messages in production
        if (event.logger === "console" && event.level !== "error" && event.level !== "warning") {
          return null;
        }
      }
      
      // In development, capture everything
      return event;
    },
    
    // Filter logs - Only capture error-level logs in production
    beforeSendLog(log) {
      if (isProduction) {
        // Only send error and warning logs in production
        if (log.level !== "error" && log.level !== "warning" && log.level !== "fatal") {
          return null;
        }
      }
      return log;
    },
    
    // Filter spans - Exclude high-frequency operations
    beforeSendSpan(span) {
      // Exclude live orders API (high-frequency polling)
      if (span.description?.includes("/v2/orders/live") || 
          span.attributes?.["http.url"]?.includes("/v2/orders/live")) {
        return null;
      }
      
      // Exclude IndexedDB operations
      if (span.op === "db.write" || span.op === "db.read" || span.op === "db.query") {
        return null;
      }
      
      // Exclude localStorage operations
      if (span.description?.includes("localStorage") || 
          span.op === "storage.local" ||
          span.attributes?.["db.system"] === "localStorage") {
        return null;
      }
      
      return span;
    },
  };
  
  Sentry.init(config);
  
  // Set initial user context if restaurantInfo is already loaded
  updateUserContext();
};

/**
 * Update user context from restaurantInfo
 * Based on actual store structure:
 * - restaurantInfo.id (UUID)
 * - restaurantInfo.name (restaurant name)
 * - restaurantInfo.email (restaurant email)
 * - restaurantInfo.contact (phone numbers)
 * - restaurantInfo.description (plan info)
 */
export const updateUserContext = () => {
  if (store.restaurantInfo) {
    // Map fields based on actual store structure
    const userId = store.restaurantInfo.id?.toString() || null;
    const email = store.restaurantInfo.email || null;
    const username = store.restaurantInfo.name || null;
    const contact = store.restaurantInfo.contact || null;
    const address = store.restaurantInfo.address || null;
    const status = store.restaurantInfo.status || null;
    
    // Set user context for Sentry
    Sentry.setUser({
      id: userId,
      email: email,
      username: username,
    });
    
    // Set additional restaurant context
    Sentry.setContext("restaurant", {
      id: userId,
      email: email,
      name: username,
      contact: contact,
      address: address,
      status: status,
      logo: store.restaurantInfo.logo,
      description: store.restaurantInfo.description, // Contains plan info
      latitude: store.restaurantInfo.latitude,
      longitude: store.restaurantInfo.longitude,
      timingFrom: store.restaurantInfo.timingFrom,
      timingTo: store.restaurantInfo.timingTo,
      createdAt: store.restaurantInfo.createdAt,
      updatedAt: store.restaurantInfo.updatedAt,
      // Partner details
      partnerCount: store.restaurantInfo.partnerDetails?.length || 0,
      enabledPartners: store.restaurantInfo.partnerDetails
        ?.filter(p => p.enabled)
        .map(p => p.partnerName)
        .join(", ") || "none",
      // Printer config
      printerCount: store.restaurantInfo.config?.printers?.length || 0,
    });
    
    // Add breadcrumb for context update
    Sentry.addBreadcrumb({
      category: "sentry.user_context",
      message: `User context updated for restaurant: ${username}`,
      level: "info",
      data: {
        restaurantId: userId,
        email: email,
        name: username,
      },
    });
  }
};

/**
 * Wrapper function to create a span for async operations
 * @param {Object} options - Span options
 * @param {string} options.name - Span name
 * @param {string} options.op - Operation type (e.g., 'http.client', 'db.query', 'function')
 * @param {string} options.function - Function name for tagging (extracted from name if not provided)
 * @param {Object} options.attributes - Additional attributes
 * @param {Function} callback - Async function to wrap
 * @returns {Promise} Result of the callback
 */
export const withSpan = async (options, callback) => {
  const { name, op = "function", function: functionName, attributes = {} } = options;
  
  // Extract function name from span name if not provided
  // Format: "API Login" -> "login", "Bluetooth Connect Printer" -> "connectPrinter"
  const extractedFunctionName = functionName || 
    name
      .toLowerCase()
      .replace(/^(api|bluetooth|db|order|printer|partner|cloud|store)\s+/i, '')
      .replace(/\s+/g, '')
      .replace(/[^a-z0-9]/g, '');
  
  return Sentry.startSpan(
    {
      name,
      op,
      attributes: {
        ...attributes,
        component: "hook",
        "function.name": extractedFunctionName,
        "function.full_name": name,
      },
    },
    async (span) => {
      // Set tag on the current scope for Sentry filtering
      Sentry.setTag("function", extractedFunctionName);
      
      try {
        const result = await callback(span);
        span?.setStatus("ok");
        return result;
      } catch (error) {
        span?.setStatus("internal_error");
        span?.setAttribute("error.type", error.constructor?.name || "Error");
        span?.setAttribute("error.message", error.message);
        Sentry.captureException(error, {
          tags: { function: extractedFunctionName },
        });
        throw error;
      }
    }
  );
};

/**
 * Wrapper for synchronous functions
 * @param {Object} options - Span options
 * @param {string} options.function - Function name for tagging
 * @param {Function} callback - Sync function to wrap
 * @returns {*} Result of the callback
 */
export const withSpanSync = (options, callback) => {
  const { name, op = "function", function: functionName, attributes = {} } = options;
  
  // Extract function name from span name if not provided
  const extractedFunctionName = functionName || 
    name
      .toLowerCase()
      .replace(/^(api|bluetooth|db|order|printer|partner|cloud|store)\s+/i, '')
      .replace(/\s+/g, '')
      .replace(/[^a-z0-9]/g, '');
  
  return Sentry.startSpan(
    {
      name,
      op,
      attributes: {
        ...attributes,
        component: "hook",
        "function.name": extractedFunctionName,
        "function.full_name": name,
      },
    },
    (span) => {
      // Set tag on the current scope for Sentry filtering
      Sentry.setTag("function", extractedFunctionName);
      
      try {
        const result = callback(span);
        span?.setStatus("ok");
        return result;
      } catch (error) {
        span?.setStatus("internal_error");
        span?.setAttribute("error.type", error.constructor?.name || "Error");
        span?.setAttribute("error.message", error.message);
        Sentry.captureException(error, {
          tags: { function: extractedFunctionName },
        });
        throw error;
      }
    }
  );
};

/**
 * Add breadcrumb for user actions
 * @param {string} message - Breadcrumb message
 * @param {string} category - Category (default: 'user.action')
 * @param {Object} data - Additional data
 * @param {string} data.function - Function name for tagging
 */
export const addBreadcrumb = (message, category = "user.action", data = {}) => {
  Sentry.addBreadcrumb({
    category,
    message,
    level: "info",
    data: {
      ...data,
      ...(data.function ? { function: data.function } : {}),
    },
  });
};

/**
 * Capture exception with context
 * @param {Error} error - Error to capture
 * @param {Object} context - Additional context
 * @param {string} context.function - Function name for tagging
 */
export const captureException = (error, context = {}) => {
  const tags = {
    ...(context.tags || {}),
  };
  
  // Add function tag if provided
  if (context.function) {
    tags.function = context.function;
  }
  
  Sentry.captureException(error, {
    tags,
    extra: context.extra || {},
  });
};

/**
 * Capture message with level
 * @param {string} message - Message to capture
 * @param {string} level - Severity level
 * @param {Object} context - Additional context
 */
export const captureMessage = (message, level = "info", context = {}) => {
  Sentry.captureMessage(message, {
    level,
    tags: context.tags || {},
    extra: context.extra || {},
  });
};

/**
 * Format request body for span attributes
 * Converts request body to a string representation suitable for Sentry spans
 * @param {any} body - Request body (can be object, FormData, string, etc.)
 * @param {number} maxLength - Maximum length before truncation (default: 10000)
 * @returns {string} - Formatted request body string
 */
export const formatRequestBody = (body, maxLength = 10000) => {
  if (!body) return "";
  
  let bodyString;
  
  if (typeof body === "string") {
    bodyString = body;
  } else if (body instanceof FormData) {
    // For FormData, convert to object representation
    const formDataObj = {};
    body.forEach((value, key) => {
      formDataObj[key] = value instanceof File ? `[File: ${value.name}, size: ${value.size} bytes]` : value;
    });
    bodyString = JSON.stringify(formDataObj);
  } else {
    try {
      bodyString = JSON.stringify(body, null, 2);
    } catch (e) {
      bodyString = String(body);
    }
  }
  
  // Truncate if too long
  if (bodyString.length > maxLength) {
    return bodyString.substring(0, maxLength) + `... [truncated, original length: ${bodyString.length}]`;
  }
  
  return bodyString;
};

/**
 * Set request body context for POST/PUT/PATCH calls
 * Similar to how restaurant info is set in context
 * @param {string} endpoint - API endpoint URL
 * @param {any} body - Request body
 * @param {string} method - HTTP method (POST, PUT, PATCH) - defaults to POST
 */
export const setRequestBodyContext = (endpoint, body, method = "POST") => {
  if (!body) return;
  
  const formattedBody = formatRequestBody(body);
  
  Sentry.setContext("request_body", {
    endpoint: endpoint,
    method: method.toUpperCase(),
    body: formattedBody,
    bodyLength: typeof formattedBody === 'string' ? formattedBody.length : 0,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Clear request body context
 */
export const clearRequestBodyContext = () => {
  Sentry.setContext("request_body", null);
};

/**
 * Set span attribute helper
 * @param {Object} span - Sentry span
 * @param {string} key - Attribute key
 * @param {*} value - Attribute value
 */
export const setSpanAttribute = (span, key, value) => {
  if (span) {
    span.setAttribute(key, value);
  }
};

/**
 * Set multiple span attributes
 * @param {Object} span - Sentry span
 * @param {Object} attributes - Attributes object
 */
export const setSpanAttributes = (span, attributes) => {
  if (span) {
    Object.entries(attributes).forEach(([key, value]) => {
      span.setAttribute(key, value);
    });
  }
};

/**
 * Create a transaction for user actions
 * @param {string} name - Transaction name
 * @param {string} op - Operation type
 * @returns {Object} Transaction object
 */
export const startTransaction = (name, op = "user.action") => {
  return Sentry.startTransaction({
    name,
    op,
  });
};

/**
 * Get current trace ID
 * @returns {string|null} Trace ID or null
 */
export const getTraceId = () => {
  try {
    const span = Sentry.getActiveSpan();
    return span?.spanContext()?.traceId || null;
  } catch (error) {
    // Fallback if getActiveSpan is not available
    return null;
  }
};

/**
 * Wrap a React hook with Sentry instrumentation
 * @param {string} hookName - Name of the hook
 * @param {Function} hookFunction - Hook function to wrap
 * @returns {Function} Wrapped hook function
 */
export const wrapHook = (hookName, hookFunction) => {
  return (...args) => {
    return Sentry.startSpan(
      {
        name: `hook.${hookName}`,
        op: "hook.execute",
        attributes: {
          hook: hookName,
        },
      },
      (span) => {
        try {
          const result = hookFunction(...args);
          span?.setStatus("ok");
          return result;
        } catch (error) {
          span?.setStatus("internal_error");
          span?.setAttribute("error.type", error.constructor?.name || "Error");
          span?.setAttribute("error.message", error.message);
          Sentry.captureException(error, {
            tags: { hook: hookName },
          });
          throw error;
        }
      }
    );
  };
};

export default {
  initSentry,
  updateUserContext,
  withSpan,
  withSpanSync,
  addBreadcrumb,
  captureException,
  captureMessage,
  setSpanAttribute,
  setSpanAttributes,
  startTransaction,
  getTraceId,
  wrapHook,
};

